import { useState, type ReactNode } from 'react';
import { Alert, Button, Card, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError } from '../api/client';
import {
  connectService,
  disconnectService,
  getServices,
  type PAService,
  type PAServices,
  type PAUnfundedDetails,
} from '../api/services';
import type { PAAccount, PAContract } from '../api/money';
import { formatCivilDate } from '../format';
import { AccountScope } from '../money/AccountScope';
import { DataTable, type Column } from '../money/DataTable';
import { Money } from '../money/Money';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import { serviceStatusLabel } from '../money/labels';
import { errorText } from '../session/errorText';

/**
 * ============================================================================
 * «ДОПОЛНИТЕЛЬНЫЕ УСЛУГИ» — §17a/b/c, F17, Appendix L §2.11
 * ============================================================================
 *
 * # ⚠ THE ONE SENTENCE THIS SCREEN MUST GET RIGHT
 *
 * «Отключить» STOPS a service and REFUNDS NOTHING. The route writes `valid_to = now`: the
 * future stops, the past is kept, the money already charged stays charged. Its operator-side
 * neighbour, `accounts.RemoveService`, emits an event whose billing reactor refunds every
 * kopeck the instance was ever charged — an eight-month router rental returned in full — and
 * the two are one word apart in Russian. So the confirmation dialog says «уже оплаченный
 * период не возвращается» in as many words: a subscriber who presses this expecting his money
 * back has been told the wrong one of the two, and he cannot undo it.
 *
 * # ⚠ WHICH ROWS GET WHICH CONTROL, AND WHY IT IS NOT «ALL OF THEM»
 *
 *   `connected`, `charge_type: 'periodic'`  → «Отключить». There is a future to stop.
 *   `connected`, `charge_type: 'one_time'`  → NO BUTTON, and a sentence saying why. The
 *       charge has already posted and the server refuses it by name
 *       (`409 pa.service_not_disconnectable`, `details.reason = "one_time"`), so a button
 *       there is a control whose only outcome is an error he cannot act on.
 *   `available`                             → «Подключить» (periodic) / «Заказать» (one-time),
 *       the two words §17b uses, chosen by the same field.
 *   `history`                               → nothing. It is over.
 *
 * # ⚠ EVERY FIGURE IS THE SERVER'S, AND `payable_now` IS NOT A SUM
 *
 * For a LINKED service (Appendix H §3.14's PACK) the connect charges the PRORATED REMAINDER of
 * the running period plus the connection cost — so `cost + connection_cost` OVERSTATES a
 * mid-month connection, and the refusal quotes the prorated figure. A screen showing the sum
 * would be refused for an amount it never named. `payable_now` comes from the engine's own
 * `PriceServiceAssignment`, i.e. from the arithmetic the debit itself reaches. ~~and
 * `src/money-arithmetic.test.ts` makes the addition unwritable anywhere in this app~~
 * *(corrected 2026-08-12: the sum WAS writable here. Cutting it in — as
 * `(cost ?? 0) + (connection_cost ?? 0)`, the shape these nullable fields invite — left that
 * guard green while this screen's own arm went red. The guard has been widened to see a
 * parenthesised operand; the claim is true again, and it is recorded rather than quietly
 * repaired because a believed guard that does not bite is worse than none.)*
 *
 * ⚠ NULL IS NOT ZERO on any of the three money fields. A tariff with no cost row at this date
 * sends `null`, and «0,00 руб» would advertise as free a service the engine cannot charge at
 * all. Those cells read «не указана»; a `payable_now` of null reads «не рассчитана» and carries
 * its own warning into the confirm dialog, because a purchase whose price we cannot state is
 * one the subscriber is being asked to make blind.
 *
 * # ⚠ THE 409 IS THE PRODUCT, NOT AN ERROR
 *
 * `accounts.service_assignment_unfunded` means «the same request will succeed the moment the
 * money lands», and its `details` name the exact shortfall. The screen prints THAT figure —
 * never a subtraction of its own, which is the D64/D74 defect this project has shipped three
 * times. ⚠ The details are DECIMAL STRINGS and not minor units (they come from the engine's
 * `money.Amount` inside a generic envelope), so they are rendered verbatim: `formatMinor`
 * would read «600.00» as six hundred kopecks.
 *
 * # WHAT IS DELIBERATELY NOT ON THIS SCREEN
 *
 *   - **A START DATE.** §17d draws «Период действия услуги» as a from–to pair and the server
 *     accepts only the end: the charge is immediate, so a future start would take money now
 *     for a period that has not begun. The UI spec's row is struck in place, and a field
 *     accepted then dropped is the dead-control class.
 *   - **ANY DAY ARITHMETIC ON `to_date`.** It is the EXCLUSIVE bound, stored and read back
 *     verbatim; the Russian wording carries «не включая» and nothing here adds or subtracts a
 *     day. A date translated on one leg of a round trip is a field that does not read back as
 *     it was written.
 *   - **A DISCOUNT FIELD.** `AddServiceInput.Discounts` exists and this surface never fills
 *     it: a subscriber does not price his own purchase.
 *   - **A «Заказать снова» BUTTON ON A HISTORY ROW.** The server's own offer list is the
 *     verdict on what may be ordered, and a one-time service he has had before appears in
 *     `available` again if it is still offered. A second door would be a client guess.
 */
export function ServicesScreen() {
  return (
    <MoneyPage titleKey="services.title" leadKey="services.lead">
      {(contract) => (
        <AccountScope
          contractID={contract.id}
          labelKey="services.accountLabel"
          emptyKey="services.noAccounts"
          selectID="pa-services-account"
        >
          {(account) => <ServicesBody contract={contract} account={account} />}
        </AccountScope>
      )}
    </MoneyPage>
  );
}

function ServicesBody({ contract, account }: { contract: PAContract; account: PAAccount }) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PAServices>(
    () => getServices(contract.id, account.account_id),
    [contract.id, account.account_id],
  );

  const [ordering, setOrdering] = useState<PAService | null>(null);
  const [quantityText, setQuantityText] = useState('1');
  const [toDateText, setToDateText] = useState('');
  const [stopping, setStopping] = useState<PAService | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [localError, setLocalError] = useState('');

  const openOrder = (row: PAService) => {
    setError(null);
    setNotice('');
    setLocalError('');
    // The server's own default, stated in the field rather than left blank: an empty box
    // beside «Количество» reads as «unknown» when the answer is one.
    setQuantityText('1');
    setToDateText('');
    setOrdering(row);
  };

  const submitOrder = async () => {
    if (!ordering) return;
    const quantity = Number(quantityText.trim());
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      setLocalError(t('services.quantityInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    setLocalError('');
    try {
      await connectService(contract.id, account.account_id, {
        service_in_tariff_id: ordering.service_in_tariff_id,
        quantity,
        // Sent ONLY when he typed one. An empty string would be a date the server has to
        // parse and refuse, and «no end» is a real and ordinary choice.
        ...(toDateText.trim() === '' ? {} : { to_date: toDateText.trim() }),
      });
      setNotice(
        t(ordering.charge_type === 'one_time' ? 'services.ordered' : 'services.connected', {
          name: ordering.name,
        }),
      );
      setOrdering(null);
    } catch (err) {
      setError(err);
      // The offer that produced the button is stale on every refusal this route makes — the
      // operator un-flagged the service, the money moved, a periodic instance already
      // started — so the lists are re-read rather than left promising the same press again.
      setOrdering(null);
    } finally {
      setBusy(false);
      // ⚠ RE-READ AND NEVER PATCH. The write's answer carries the instance and NO price list
      // at all (`serviceRow` fills the ids, the dates and the status and leaves the name, the
      // costs and the quote empty), so the three lists come from the server or from nowhere.
      reload();
    }
  };

  const submitStop = async () => {
    if (!stopping) return;
    setBusy(true);
    setError(null);
    setNotice('');
    try {
      await disconnectService(contract.id, account.account_id, stopping.account_service_id);
      setNotice(t('services.disconnected', { name: stopping.name }));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
      setStopping(null);
      reload();
    }
  };

  return (
    <QueryFrame query={query} reload={reload}>
      {(data) => (
        <>
          {notice ? (
            <Alert type="success" showIcon message={notice} style={{ marginBottom: 16 }} />
          ) : null}
          {error ? (
            <Alert
              type="error"
              showIcon
              message={errorText(error, t)}
              description={shortfallNote(error, t)}
              style={{ marginBottom: 16 }}
            />
          ) : null}

          <Card className="pa-block" title={t('services.connectedTitle')}>
            <DataTable
              columns={connectedColumns(contract.currency, t, setStopping)}
              rows={data.connected}
              rowKey={(s) => s.account_service_id}
              emptyKey="services.connectedEmpty"
              detail={(s) => serviceDetail(s, contract.currency, t)}
            />
          </Card>

          <Card className="pa-block" title={t('services.availableTitle')}>
            <DataTable
              columns={availableColumns(contract.currency, t, openOrder)}
              rows={data.available}
              rowKey={(s) => s.service_in_tariff_id}
              emptyKey="services.availableEmpty"
              detail={(s) => (s.description ? <span>{s.description}</span> : null)}
            />
            {/* Said once, under the list rather than per row: §17b's own rule. */}
            <p className="pa-field-hint">{t('services.availableHint')}</p>
          </Card>

          <Card className="pa-block" title={t('services.historyTitle')}>
            <DataTable
              columns={historyColumns(contract.currency, t)}
              rows={data.history}
              rowKey={(s) => s.account_service_id}
              emptyKey="services.historyEmpty"
            />
          </Card>

          <Modal
            open={ordering !== null}
            title={t(
              ordering?.charge_type === 'one_time' ? 'services.orderTitle' : 'services.connectTitle',
            )}
            okText={t(ordering?.charge_type === 'one_time' ? 'services.order' : 'services.connect')}
            cancelText={t('services.cancel')}
            okButtonProps={{ loading: busy }}
            onOk={() => void submitOrder()}
            onCancel={() => {
              setOrdering(null);
              setLocalError('');
            }}
          >
            {ordering ? (
              <OrderForm
                row={ordering}
                login={data.login}
                currency={contract.currency}
                endDateOffered={data.ordering_supports_end_date}
                quantityText={quantityText}
                toDateText={toDateText}
                localError={localError}
                onQuantity={(next) => {
                  setLocalError('');
                  setQuantityText(next);
                }}
                onToDate={(next) => {
                  setLocalError('');
                  setToDateText(next);
                }}
              />
            ) : null}
          </Modal>

          <Modal
            open={stopping !== null}
            title={t('services.disconnectTitle')}
            okText={t('services.disconnect')}
            cancelText={t('services.cancel')}
            okButtonProps={{ danger: true, loading: busy }}
            onOk={() => void submitStop()}
            onCancel={() => setStopping(null)}
          >
            <p className="pa-contact-value">{stopping?.name}</p>
            <ul className="pa-warnings">
              <li>{t('services.disconnectStops')}</li>
              {/* ⚠ THE SENTENCE THE WHOLE FILE IS ABOUT. See the header. */}
              <li>{t('services.disconnectNoRefund')}</li>
              <li>{t('services.disconnectAgain')}</li>
            </ul>
          </Modal>
        </>
      )}
    </QueryFrame>
  );
}

/**
 * shortfallNote prints the figure the SERVER named on an unfunded assignment.
 *
 * ⚠ IT PRINTS `details.shortfall` AND COMPUTES NOTHING. «Сколько внести, чтобы получилось» is
 * the one figure this project has had wrong three times on three surfaces, each of which
 * derived it (D64/D74 «требуется минус доступно», D67 the fee instead of the pack, and a
 * per-Account share beside a contract-wide gate). The engine's linked-assignment gate tests
 * SPENDABLE — availability minus the money already reserved for the upcoming boundary charge —
 * so no subtraction available in this browser reproduces it.
 *
 * ⚠ AND THE VALUE IS A DECIMAL STRING, printed verbatim. It reaches the browser inside a
 * generic error envelope rather than as a wire field, so it is «273.50» and not 27350:
 * handing it to `formatMinor` would quote «273,50» as «2,74».
 */
function shortfallNote(err: unknown, t: TFunction): ReactNode {
  if (!(err instanceof ApiError)) return null;
  if (err.code !== 'accounts.service_assignment_unfunded') return null;
  const details = (err.details ?? {}) as PAUnfundedDetails;
  if (!details.shortfall) return null;
  return <span>{t('services.shortfall', { amount: details.shortfall })}</span>;
}

/**
 * priceCell renders one price-list figure — or says it is not configured.
 *
 * ⚠ «не указана» AND NEVER «0,00». `null` on `cost` / `connection_cost` means the tariff
 * carries no cost row at this date, which is a real state the operator has to fix; printing a
 * zero would advertise the service as free and the engine would then refuse to charge it.
 */
function priceCell(minor: number | null, currency: string, t: TFunction): ReactNode {
  if (minor === null) return <span className="pa-field-hint">{t('services.priceUnset')}</span>;
  return <Money minor={minor} currency={currency} />;
}

function connectedColumns(
  currency: string,
  t: TFunction,
  onStop: (row: PAService) => void,
): ReadonlyArray<Column<PAService>> {
  return [
    { key: 'name', labelKey: 'services.columnName', track: 'minmax(0, 2fr)', render: (s) => s.name },
    { key: 'tariff', labelKey: 'services.columnTariff', render: (s) => s.tariff_name },
    { key: 'from', labelKey: 'services.columnFrom', render: (s) => formatCivilDate(s.from_date) },
    {
      key: 'cost',
      labelKey: 'services.columnCost',
      className: 'pa-td-right',
      render: (s) => priceCell(s.cost, currency, t),
    },
    {
      key: 'status',
      labelKey: 'services.columnStatus',
      render: (s) => serviceStatusLabel(s.status, t),
    },
    {
      key: 'action',
      labelKey: 'services.columnAction',
      // ⚠ THE ONE PLACE THE `charge_type` DECIDES A CONTROL RATHER THAN A WORD. A one-time
      // instance gets the sentence and no button — see the file header.
      render: (s) =>
        s.charge_type === 'periodic' ? (
          <Button size="large" onClick={() => onStop(s)}>
            {t('services.disconnect')}
          </Button>
        ) : (
          <span className="pa-field-hint">{t('services.oneTimeNoDisconnect')}</span>
        ),
    },
  ];
}

function availableColumns(
  currency: string,
  t: TFunction,
  onOrder: (row: PAService) => void,
): ReadonlyArray<Column<PAService>> {
  return [
    { key: 'name', labelKey: 'services.columnName', track: 'minmax(0, 2fr)', render: (s) => s.name },
    { key: 'tariff', labelKey: 'services.columnTariff', render: (s) => s.tariff_name },
    {
      key: 'connection',
      labelKey: 'services.columnConnectionCost',
      className: 'pa-td-right',
      render: (s) => priceCell(s.connection_cost, currency, t),
    },
    {
      key: 'cost',
      labelKey: 'services.columnCost',
      className: 'pa-td-right',
      render: (s) => priceCell(s.cost, currency, t),
    },
    {
      // THE ENGINE'S FIGURE, in its own column beside the price list precisely so the two
      // can be seen to differ — which for a linked mid-month connection they do.
      key: 'payable',
      labelKey: 'services.columnPayableNow',
      className: 'pa-td-right',
      render: (s) =>
        s.payable_now === null ? (
          <span className="pa-field-hint">{t('services.payableUnknown')}</span>
        ) : (
          <Money minor={s.payable_now} currency={currency} strong />
        ),
    },
    {
      key: 'action',
      labelKey: 'services.columnAction',
      render: (s) => (
        <Button type="primary" size="large" onClick={() => onOrder(s)}>
          {t(s.charge_type === 'one_time' ? 'services.order' : 'services.connect')}
        </Button>
      ),
    },
  ];
}

function historyColumns(currency: string, t: TFunction): ReadonlyArray<Column<PAService>> {
  return [
    { key: 'name', labelKey: 'services.columnName', track: 'minmax(0, 2fr)', render: (s) => s.name },
    { key: 'tariff', labelKey: 'services.columnTariff', render: (s) => s.tariff_name },
    { key: 'from', labelKey: 'services.columnFrom', render: (s) => formatCivilDate(s.from_date) },
    {
      // ⚠ THE EXCLUSIVE BOUND, PRINTED VERBATIM. The header's own label says «не включая», so
      // no day is added or subtracted here — see the file header for why that matters.
      key: 'to',
      labelKey: 'services.columnTo',
      render: (s) => formatCivilDate(s.to_date),
    },
    {
      key: 'cost',
      labelKey: 'services.columnCost',
      className: 'pa-td-right',
      render: (s) => priceCell(s.cost, currency, t),
    },
  ];
}

/**
 * The detail line under a CONNECTED row: its description, its connection cost, its end date
 * if it has one, and whether the subscriber connected it himself.
 *
 * The end date lives here rather than in a column because most instances have none, and a
 * mostly-empty «До» column at 360 px costs a whole card line on every row to serve a few.
 */
function serviceDetail(s: PAService, currency: string, t: TFunction): ReactNode {
  const parts: ReactNode[] = [];
  if (s.description) parts.push(<span key="desc">{s.description}</span>);
  parts.push(
    <span key="connection">
      {t('services.connectionCostInline')} {priceCell(s.connection_cost, currency, t)}
    </span>,
  );
  if (s.to_date) {
    parts.push(
      <span key="to">{t('services.untilExclusive', { date: formatCivilDate(s.to_date) })}</span>,
    );
  }
  if (s.quantity > 1) {
    parts.push(<span key="qty">{t('services.quantityInline', { count: s.quantity })}</span>);
  }
  if (s.self_assigned) parts.push(<span key="self">{t('services.selfAssigned')}</span>);
  // A FRAGMENT and not a wrapper: `.pa-table-detail` is already the flex column that lays
  // these out, at both viewports, so an extra element would need its own rule to look the
  // same — and the one-markup rule `DataTable` states is what keeps that from happening.
  return <>{parts}</>;
}

/** The order form: what he is buying, what it costs NOW, and the two fields §17d asks for. */
function OrderForm({
  row,
  login,
  currency,
  endDateOffered,
  quantityText,
  toDateText,
  localError,
  onQuantity,
  onToDate,
}: {
  row: PAService;
  login: string;
  currency: string;
  endDateOffered: boolean;
  quantityText: string;
  toDateText: string;
  localError: string;
  onQuantity: (next: string) => void;
  onToDate: (next: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <span className="pa-field-label">{t('services.accountLabel')}</span>
      <p className="pa-contact-value">
        <span className="mono">{login}</span>
      </p>

      <span className="pa-field-label">{t('services.columnName')}</span>
      <p className="pa-contact-value">{row.name}</p>
      {row.description ? <p className="pa-field-hint">{row.description}</p> : null}

      <span className="pa-field-label">{t('services.columnTariff')}</span>
      <p className="pa-contact-value">{row.tariff_name}</p>

      <span className="pa-field-label">{t('services.columnPayableNow')}</span>
      <p className="pa-contact-value">
        {row.payable_now === null ? (
          <span>{t('services.payableUnknown')}</span>
        ) : (
          <Money minor={row.payable_now} currency={currency} strong />
        )}
      </p>
      {/* ⚠ THE WARNING THAT GOES WITH A PRICE WE COULD NOT STATE. It is not a refusal: for a
          service that is NOT part of the subscription pack the connect succeeds and is charged
          from the price list, so withholding the button would hide a working control. What it
          must not do is look like a confirmed figure. */}
      {row.payable_now === null ? (
        <Alert
          type="warning"
          showIcon
          message={t('services.payableUnknownNote')}
          style={{ marginBottom: 12 }}
        />
      ) : null}
      {/* The engine prices a LINKED service as the remainder of the running period, which is
          why it is not the price list's sum. Said only where it applies. */}
      {row.linked ? <p className="pa-field-hint">{t('services.linkedNote')}</p> : null}

      <label className="pa-field-label" htmlFor="pa-service-quantity">
        {t('services.quantityLabel')}
      </label>
      <Input
        id="pa-service-quantity"
        type="number"
        min={1}
        size="large"
        value={quantityText}
        status={localError ? 'error' : undefined}
        onChange={(e) => onQuantity(e.target.value)}
      />
      {localError ? <p className="pa-field-error">{localError}</p> : null}

      {/* ⚠ RENDERED FROM `ordering_supports_end_date` AND NOT FROM A CONSTANT. The server
          states which fields the form may send; a date box a later build stopped reading
          would otherwise be a value the subscriber chose and nothing applied. There is NO
          start-date box beside it — see the file header. */}
      {endDateOffered ? (
        <>
          <label className="pa-field-label" htmlFor="pa-service-to">
            {t('services.toDateLabel')}
          </label>
          <Input
            id="pa-service-to"
            type="date"
            size="large"
            value={toDateText}
            onChange={(e) => onToDate(e.target.value)}
          />
          <p className="pa-field-hint">{t('services.toDateHint')}</p>
        </>
      ) : null}

      <Alert type="info" showIcon message={t('services.chargeNow')} style={{ marginTop: 12 }} />
    </>
  );
}
